/**
 * Human ("natural") ordering for identifiers that embed numbers.
 *
 * Building levels are the case that forced this. The api-server derives
 * a site's level list by scanning Zenoh keys and returning
 * `sorted(levels)` — a plain lexicographic sort, where a string
 * compares character by character and "1" < "4". For sg-nomad-01 that
 * turns the physical stack
 *
 *   L4, L4M, L6, L7, L8, L9, L10, L11, … L17
 *
 * into
 *
 *   L10, L11, … L17, L4, L4M, L6, … L9
 *
 * so the floor picker opened on L10 and read as if the building started
 * halfway up. Nothing is wrong with the backend's sort — it just has no
 * way to know a digit run is a NUMBER. That is exactly what
 * `Intl`-backed collation with `numeric: true` adds: digit runs compare
 * by value, everything else compares as text.
 *
 * `sensitivity: 'base'` makes the comparison case- and accent-
 * insensitive, so a site that names a mezzanine `L4m` sorts next to
 * `L4M` instead of into a separate block by ASCII case. Ordering stays
 * total (equal-comparing distinct strings keep their input order under
 * a stable sort, which `Array.prototype.sort` has been since ES2019).
 *
 * Locale is deliberately left `undefined` = the runtime's default: this
 * orders operator-facing labels for whoever is looking at the screen,
 * and the digit-run rule that does the real work here is locale-
 * independent.
 */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * `naturalCompare` applied to a whole list, without mutating the input.
 *
 * Callers are usually holding a TanStack Query result array, which is
 * cache-owned and must not be sorted in place — an in-place
 * `.sort()` there mutates the cached value every other consumer reads.
 */
export function naturalSort(values: readonly string[]): string[] {
  return [...values].sort(naturalCompare);
}
